import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { CheckCircle, Package, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { token } = useAuth();
  const { clearCart } = useCart();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) return;
    // Wait for token to be available from localStorage
    const savedToken = token || localStorage.getItem('aura_token');
    if (!savedToken) {
      if (attempts < 10) {
        setTimeout(() => setAttempts(a => a + 1), 1000);
      } else {
        setStatus('error');
      }
      return;
    }
    const pollStatus = async () => {
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`, { headers: { Authorization: `Bearer ${savedToken}` } });
        if (res.data.payment_status === 'paid') {
          setStatus('paid');
          clearCart();
        } else if (res.data.status === 'expired') {
          setStatus('expired');
        } else if (attempts < 10) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('timeout');
        }
      } catch {
        if (attempts < 10) setTimeout(() => setAttempts(a => a + 1), 2000);
        else setStatus('error');
      }
    };
    pollStatus();
  }, [sessionId, token, attempts, clearCart]);

  return (
    <div className="min-h-screen bg-[var(--cream)] flex items-center justify-center px-4" data-testid="checkout-success-page">
      <div className="max-w-md w-full text-center space-y-6">
        {status === 'checking' && (
          <>
            <div className="animate-spin w-12 h-12 border-3 border-[var(--violet)] border-t-transparent rounded-full mx-auto"></div>
            <h2 className="text-2xl font-bold text-[var(--dark)]">Verificando pago...</h2>
            <p className="text-[var(--dark)]/50">Estamos confirmando tu pago, esto tomara unos segundos.</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <div className="w-20 h-20 bg-[var(--sage-light)] rounded-full flex items-center justify-center mx-auto">
              <CheckCircle className="h-10 w-10 text-[var(--sage-dark)]" />
            </div>
            <h2 className="text-2xl font-bold text-[var(--dark)]">Pago exitoso!</h2>
            <p className="text-[var(--dark)]/50">Tu pedido ha sido confirmado. Pronto recibiras mas informacion.</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
              <Link to="/mis-pedidos"><Button className="bg-[var(--violet)] text-white rounded-full px-6" data-testid="view-orders-btn"><Package className="mr-2 h-4 w-4" /> Ver mis pedidos</Button></Link>
              <Link to="/tienda"><Button variant="outline" className="border-[var(--sage)] rounded-full px-6" data-testid="continue-shopping-btn">Seguir comprando <ArrowRight className="ml-2 h-4 w-4" /></Button></Link>
            </div>
          </>
        )}
        {(status === 'expired' || status === 'error' || status === 'timeout') && (
          <>
            <h2 className="text-2xl font-bold text-[var(--dark)]">Hubo un problema</h2>
            <p className="text-[var(--dark)]/50">No pudimos confirmar tu pago. Por favor contactanos si necesitas ayuda.</p>
            <Link to="/tienda"><Button className="bg-[var(--violet)] text-white rounded-full px-6">Volver a la tienda</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
